import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { PromiseStatuses } from "../../../../lib/utils";
import { ContactCreateDTO, ContactDTO, ContactUpdateDTO } from "./dto";
import { NewContactService } from "./service";

interface ContactState {
    contactCreateRequest: ContactCreateDTO
    contactCreateStatus: PromiseStatuses
    contactUpdateRequest: ContactUpdateDTO
    contactUpdateStatus: PromiseStatuses
    contactCreateErrors: Partial<Record<keyof ContactCreateDTO, string>>
    getContactByIdStatus: PromiseStatuses
    getContactByIdResponse?: ContactDTO
}

const initialState: ContactState = {
    contactCreateRequest: {
        phone: "",
        officePhone: "",
        mobilePhone: "",
        fax: "",
        email: "",
        personalEmail: "",
        pec: "",
        otherEmail: "",
        note: "",
        customerId: ""
    },
    contactCreateStatus: "idle",
    contactUpdateRequest: {
        phone: "",
        officePhone: "",
        mobilePhone: "",
        fax: "",
        email: "",
        personalEmail: "",
        pec: "",
        otherEmail: "",
        note: ""
    },
    contactUpdateStatus: "idle",
    contactCreateErrors: {},
    getContactByIdStatus: "idle"
}

export const validateContactCreateRequest = createAsyncThunk(
    'contact/validateCreateRequest',
    async (request: ContactCreateDTO, thunkApi): Promise<void> => {
        const errors: Partial<Record<keyof ContactCreateDTO, string>> = {}

        const requiredStrings: { field: keyof ContactCreateDTO; label: string }[] = [
            { field: 'phone', label: 'Telefono' },
            { field: 'mobilePhone', label: 'Cellulare' },
            { field: 'officePhone', label: 'Telefono Ufficio' },
        ]

        for (const { field, label } of requiredStrings) {
            const value = request[field]
            if (!value || (typeof value === 'string' && value.trim() === '')) {
                errors[field] = `${label} è obbligatorio`
            }
        }

        const phone = request.phone?.trim() ?? ''
        if (phone && (phone.length < 9 || phone.length > 13)) {
            errors.phone = 'Telefono deve avere tra 9 e 13 caratteri'
        }

        const mobilePhone = request.mobilePhone?.trim() ?? ''
        if (mobilePhone && (mobilePhone.length < 9 || mobilePhone.length > 13)) {
            errors.mobilePhone = 'Cellulare deve avere tra 9 e 13 caratteri'
        }
        const officePhone = request.officePhone?.trim() ?? ''
        if (officePhone && (officePhone.length < 9 || officePhone.length > 13)) {
            errors.officePhone = 'Telefono Ufficio deve avere tra 9 e 13 caratteri'
        }

        if (Object.keys(errors).length > 0) {
            return thunkApi.rejectWithValue(errors) as never
        }
    }
)

export const getContactById = createAsyncThunk(
    'contact/getContactById',
    async (id: string, thunkApi): Promise<ContactDTO> => {
        const contactService = NewContactService()

        return contactService.getContactById(id).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })

    },
)

export const createContact = createAsyncThunk(
    'contact/createContact',
    async (request: ContactCreateDTO, thunkApi): Promise<number> => {
        const contactService = NewContactService()

        return contactService.createContact(request).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })

    },
)

export const updateContact = createAsyncThunk(
    'contact/updateContact',
    async (request: { id: string, data: ContactUpdateDTO }, thunkApi): Promise<void> => {
        const contactService = NewContactService()

        return contactService.updateContact(request.id, request.data).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })

    },
)

const contactSlice = createSlice({
    name: 'contact/slice',
    initialState,
    reducers: {
        setContactCreateRequestField: (state, action: { payload: { field: keyof ContactCreateDTO, value: any } }) => {
            state.contactCreateRequest = {
                ...state.contactCreateRequest,
                [action.payload.field]: action.payload.value
            }
        },
        setContactUpdateRequest: (state, action) => {
            state.contactUpdateRequest = action.payload
        },
        setContactUpdateRequestField: (state, action: { payload: { field: keyof ContactUpdateDTO, value: any } }) => {
            state.contactUpdateRequest = {
                ...state.contactUpdateRequest,
                [action.payload.field]: action.payload.value
            }
        },
        resetContactCreateRequest: (state) => {
            state.contactCreateRequest = initialState.contactCreateRequest
        },
        resetContactUpdateRequest: (state) => {
            state.contactUpdateRequest = initialState.contactUpdateRequest
        },
        setContactCreateErrors: (state, action: { payload: Partial<Record<keyof ContactCreateDTO, string>> }) => {
            state.contactCreateErrors = action.payload
        },
        resetContactCreateErrors: (state) => {
            state.contactCreateErrors = {}
        },
        resetContactCreateError: (state, action: { payload: keyof ContactCreateDTO }) => {
            delete state.contactCreateErrors[action.payload]
        },
        setContactCreateStatus: (state, action) => {
            state.contactCreateStatus = action.payload
        },
        setContactUpdateStatus: (state, action) => {
            state.contactUpdateStatus = action.payload
        },
        resetContactById: (state) => {
            state.getContactByIdStatus = "idle"
            state.getContactByIdResponse = undefined
        }
    },
    extraReducers(builder) {
        builder
            .addCase(getContactById.pending, (state) => {
                state.getContactByIdStatus = "loading"
            })
            .addCase(getContactById.fulfilled, (state, action) => {
                state.getContactByIdStatus = "successfully"
                state.getContactByIdResponse = action.payload
            })
            .addCase(getContactById.rejected, (state) => {
                state.getContactByIdStatus = "failed"
            })
            .addCase(createContact.pending, (state) => {
                state.contactCreateStatus = "loading"
            })
            .addCase(createContact.fulfilled, (state) => {
                state.contactCreateStatus = "successfully"
            })
            .addCase(createContact.rejected, (state) => {
                state.contactCreateStatus = "failed"
            })
            .addCase(updateContact.pending, (state) => {
                state.contactUpdateStatus = "loading"
            })
            .addCase(updateContact.fulfilled, (state) => {
                state.contactUpdateStatus = "successfully"
            })
            .addCase(updateContact.rejected, (state) => {
                state.contactUpdateStatus = "failed"
            })
            .addCase(validateContactCreateRequest.fulfilled, (state) => {
                state.contactCreateErrors = {}
            })
            .addCase(validateContactCreateRequest.rejected, (state, action) => {
                state.contactCreateErrors = action.payload ?? {}
            })
    },
})

export const {
    setContactCreateRequestField,
    setContactUpdateRequest,
    setContactUpdateRequestField,
    resetContactCreateRequest,
    resetContactUpdateRequest,
    setContactCreateErrors,
    resetContactCreateErrors,
    resetContactCreateError,
    setContactCreateStatus,
    setContactUpdateStatus,
    resetContactById
} = contactSlice.actions

export default contactSlice.reducer